import type { MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { getAuthUser } from "~/services/auth.server";

export const meta: MetaFunction = () => {
  return [
    { title: "이용약관 - 슬로우다이브" },
    { name: "description", content: "슬로우다이브 서비스 이용약관입니다." },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);

  return json({
    user,
  });
}

export default function TermsPage() {
  const { user } = useLoaderData<typeof loader>();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li>
              <a
                href="/"
                className="hover:text-gray-700 dark:hover:text-gray-300"
              >
                홈
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900 dark:text-white font-medium">
              이용약관
            </li>
          </ol>
        </nav>

        {/* Main content */}
        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                슬로우다이브 이용약관
              </h1>

              {/* Highlight box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-8">
                <p className="text-blue-800 dark:text-blue-200 font-medium">
                  슬로우다이브 이용약관 (전자상거래법 기반 통신판매업 약관)
                </p>
              </div>

              <div className="space-y-8 text-gray-800 dark:text-gray-200">
                {/* 제1조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제1조 (목적)
                  </h2>
                  <p className="leading-relaxed">
                    본 약관은 슬로우다이브(이하 "회사")가 운영하는 온라인
                    플랫폼(https://slowdive.kr, 이하 "몰")을 통해 제공하는 식단,
                    정보서비스 및 콘텐츠 판매와 관련하여, 회사와 이용자 간의
                    권리, 의무 및 책임사항을 정함을 목적으로 합니다.
                  </p>
                </section>

                {/* 제2조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제2조 (정의)
                  </h2>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        • 몰:
                      </span>
                      <span>
                        회사가 운영하는 웹사이트 및 앱을 통해 식단, 정보서비스
                        및 콘텐츠를 판매하는 온라인 플랫폼
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        • 이용자:
                      </span>
                      <span>
                        몰에 접속하여 본 약관에 따라 회사가 제공하는 서비스를
                        이용하는 자
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        • 회원:
                      </span>
                      <span>
                        몰에 개인정보를 등록하여 서비스를 정기적으로 이용할 수
                        있는 자
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        • 비회원:
                      </span>
                      <span>회원가입 없이 일시적으로 서비스를 이용하는 자</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        • 콘텐츠:
                      </span>
                      <span>
                        온라인 식단, 정보서비스 및 콘텐츠, 기타 회사가 제공하는
                        상품
                      </span>
                    </li>
                  </ul>
                </section>

                {/* 제3조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제3조 (약관의 게시 및 개정)
                  </h2>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      회사는 본 약관을 몰의 초기화면 또는 연결화면을 통해
                      이용자가 확인할 수 있도록 게시합니다.
                    </li>
                    <li>
                      약관 변경 시 회사는 최소 7일 전 공지하며, 불리한 변경 시
                      30일 이상 사전 공지합니다.
                    </li>
                    <li>
                      변경된 약관은 공지된 적용일 이후 체결되는 계약에 한해
                      적용됩니다.
                    </li>
                  </ol>
                </section>

                {/* 제4조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제4조 (서비스 제공 및 변경)
                  </h2>
                  <p className="mb-4">
                    회사는 다음 각 호의 서비스를 제공합니다.
                  </p>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>식단, 정보서비스 및 콘텐츠 판매 및 이용 권한 제공</li>
                    <li>기타 회사가 정하는 부가서비스</li>
                  </ol>
                  <p>
                    콘텐츠 구성이나 제공 방식은 사전 고지 후 변경될 수 있습니다.
                  </p>
                </section>

                {/* 제5조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제5조 (결제 및 이용 조건)
                  </h2>
                  <p className="mb-4">
                    이용자는 회사와 계약된 결제 서비스를 통해 다음과 같은
                    방식으로 결제할 수 있습니다.
                  </p>
                  <ul className="list-disc list-inside mb-4">
                    <li>
                      신용카드, 가상계좌, 계좌이체, 무통장입금, 간편결제 등
                    </li>
                  </ul>
                  <p>
                    회사는 별도의 결제수수료를 부과하지 않으며, 결제 완료 시
                    서비스 이용 권한이 부여됩니다.
                  </p>
                </section>

                {/* 제6조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제6조 (환불 및 청약철회)
                  </h2>
                  <p className="mb-4">
                    결제일로부터 7일 이내, 콘텐츠 이용 또는 다운로드가 없을 경우
                    전액 환불 가능합니다.
                  </p>
                  <p className="mb-4">
                    다음 항목에 해당하는 경우 환불이 제한됩니다.
                  </p>
                  <ol className="list-decimal list-inside mb-4">
                    <li>
                      유료 식단, 정보서비스 및 콘텐츠 이용 또는 다운로드 후
                    </li>
                  </ol>
                  <p className="mb-4">
                    콘텐츠 자체의 하자 또는 서비스 오류가 확인된 경우, 전액
                    환불을 제공합니다.
                  </p>
                  <p>환불은 접수 후 영업일 기준 3일 이내에 처리됩니다.</p>
                </section>

                {/* 제7조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제7조 (저작권 및 이용 제한)
                  </h2>
                  <p className="mb-4">
                    회사가 제공하는 콘텐츠 및 자료의 저작권은 회사에 귀속되며,
                    사전 서면 동의 없이 복제, 배포, 2차 사용을 금합니다.
                  </p>
                  <p>
                    이용자는 해당 콘텐츠를 개인 학습 목적 외의 용도로 사용할 수
                    없습니다.
                  </p>
                </section>

                {/* 제8조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제8조 (개인정보 보호)
                  </h2>
                  <p className="mb-4">
                    회사는 서비스 제공에 필요한 최소한의 개인정보만 수집하며,
                    동의 없이 제3자에게 제공하지 않습니다.
                  </p>
                  <p>
                    개인정보 처리 방침은 홈페이지 내 별도 페이지에 게시하며,
                    회원은 언제든지 열람·수정·삭제 요청이 가능합니다.
                  </p>
                </section>

                {/* 제9조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제9조 (서비스 중단 및 보상)
                  </h2>
                  <p className="mb-4">
                    정기 점검, 긴급 시스템 유지 등으로 서비스 일시 중단이 있을
                    수 있으며, 사전에 공지합니다.
                  </p>
                  <p>
                    회사의 귀책으로 서비스 제공이 불가능할 경우, 이용자에게 해당
                    금액 비례 환불을 제공합니다.
                  </p>
                </section>

                {/* 제10조 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    제10조 (분쟁 해결 및 관할)
                  </h2>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        •
                      </span>
                      <span>
                        이용자의 불만 및 피해 구제를 위해 고객센터를 운영하며,
                        신속하게 대응합니다.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        •
                      </span>
                      <span>
                        전자상거래 분쟁 발생 시 공정거래위원회 또는
                        분쟁조정기구의 조정에 따를 수 있습니다.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        •
                      </span>
                      <span>
                        본 약관과 관련한 분쟁은 회사 주소지 관할 법원을 제1심
                        전속관할로 합니다.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-gray-900 dark:text-white mr-2">
                        •
                      </span>
                      <span>준거법은 대한민국 법으로 합니다.</span>
                    </li>
                  </ul>
                </section>

                {/* 부칙 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    부칙
                  </h2>
                  <p className="mb-4">
                    본 약관은 2025년 8월 1일부터 적용됩니다.
                  </p>
                  <p>
                    약관 변경 시 별도 공지 없이 적용일 기준으로 자동 갱신됩니다.
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  © 2025 slowdive | All rights reserved.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  본 이용약관은 당사의 서비스를 이용하는 모든 이용자에게
                  적용됩니다.
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
